import type { PostHog } from "posthog-js";
import { AnalyticsEvent, AnalyticsIdentity, AnalyticsInitConfig, AnalyticsProperties, AnalyticsProvider } from "../types";

type PostHogClient = PostHog;
type PendingOperation = () => void;

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";
const queue: PendingOperation[] = [];
let posthogClient: PostHogClient | null = null;
let initialized = false;
let loading = false;
let disabled = false;

const flush = () => {
  while (queue.length) {
    const operation = queue.shift();
    operation?.();
  }
};

const runWhenReady = (operation: PendingOperation) => {
  if (disabled) {
    return;
  }

  if (!initialized || !posthogClient) {
    queue.push(operation);
    return;
  }

  operation();
};

export const postHogProvider: AnalyticsProvider = {
  name: "posthog",
  init(config: AnalyticsInitConfig) {
    const token = config.postHogToken;
    if (!token || initialized || loading) {
      disabled = !token && !initialized;
      return;
    }

    disabled = false;
    loading = true;

    import("posthog-js")
      .then((module) => {
        posthogClient = module.default;
        posthogClient.init(token, {
          api_host: config.postHogHost || DEFAULT_POSTHOG_HOST,
          defaults: "2026-01-30",
          capture_pageview: false,
          autocapture: false
        });
        initialized = true;
        flush();
      })
      .finally(() => {
        loading = false;
      });
  },
  track(event: AnalyticsEvent) {
    runWhenReady(() => posthogClient?.capture(event.name, event.properties || {}));
  },
  identify(identity: AnalyticsIdentity) {
    runWhenReady(() => posthogClient?.identify(identity.userId, identity.traits || {}));
  },
  setUserProperties(properties: AnalyticsProperties) {
    runWhenReady(() => {
      const client: any = posthogClient;
      if (client?.setPersonProperties) {
        client.setPersonProperties(properties);
      } else if (client?.people?.set) {
        client.people.set(properties);
      }
    });
  },
  reset() {
    runWhenReady(() => posthogClient?.reset());
  }
};
